/* eslint-disable import/no-cycle */
import {useState} from 'react';
import {useTranslation} from 'react-i18next';
import {FontAwesomeIcon} from '@fortawesome/react-fontawesome';
import {faBagsShopping} from '@fortawesome/pro-solid-svg-icons';
import {observer} from 'mobx-react-lite';
import {Button, Col, Row} from 'antd';
import {sortBy} from 'lodash';
import moment from 'moment';
import Modal from '../shared/modal/Modal';
import {OrderDetails} from './OrderDetails';
import OrderTable from './OrderTable';
import {useMount} from '../../hooks/useMount';
import {ActiveOperationWidgetConfig} from '../operation/activeOperationWidgetConfig';
import ActiveOperationDetailsWidget from '../operation/ActiveOperationDetailsWidget';
import Popconfirm from '../shared/messaging/Popconfirm';
import {TransitionStoreActions} from '../../stores/transitionStore';
import {StateTransitionWidgetDetailModal} from '../stateTransition/StateTransitionWidgetDetailModal';
import {useStore} from '../../hooks/useStore';
import {useModulePolicy} from '../../hooks/useModulePolicy';
import {transition} from '../../models/scope';
import {Operation} from '../../models/operation';

const OrderWidgetModal = ({
  identifier,
  settings,
  columnsPlanned,
  columnsFinished,
  workplaceId,
  useSelectedOperation = [undefined, () => undefined],
  useIsReordering = [undefined, () => false],
  onCancel,
  ...props
}) => {
  const store = useStore();
  const {t} = useTranslation();
  const [selectedOperation, setSelectedOperation] = useSelectedOperation;
  const [showOperationDetails, setShowOperationDetails] = useState(false);
  const widgetConfig = new ActiveOperationWidgetConfig(store, ActiveOperationDetailsWidget.identifier);
  const [transitionConfirmVisible, setTransitionConfirmVisible] = useState(false);
  const [modalVisible, setModalVisible] = useState(false);
  const [currentStartTransitionId, setCurrentStartTransitionId] = useState();
  const [currentOperationId, setCurrentOperationId] = useState();
  const [lastStateChange, setLastStateChange] = useState(null);
  const [isReordering, setIsReordering] = useIsReordering;
  const [forcedStart, setForcedStart] = useState(false);
  const policy = useModulePolicy(store, [transition]);
  const startAnyPolicy = useModulePolicy(store, ['start-any-operation']);

  useMount(() => {
    if (selectedOperation?.id) {
      store.validStartTransitionsStore.loadValidStartTransitions(selectedOperation.id);
      setTransitionConfirmVisible(false);

      if (selectedOperation.orderId) {
        store.orderStore.load(selectedOperation.orderId);
        store.operationStore.loadAllWithDependencies({params: {orderId: selectedOperation.orderId}});

        store.operationStateLogStore.getByWorkplaceId(store.workplaceStore.selectedWorkplace?.id);
        store.workplaceStateLogStore.getByWorkplaceId(store.workplaceStore.selectedWorkplace?.id);
      }
    }
  }, [selectedOperation, store.workplaceStore.selectedWorkplace?.id]);

  useMount(() => {
    let latestChange = store.operationStateLogStore.getLatestOfWorkplace(workplaceId);
    const latestWpStateChange = store.workplaceStateLogStore.getLatestOfWorkplace(workplaceId);

    if ((!latestChange && latestWpStateChange)
      || (latestChange && latestWpStateChange && latestChange.start < latestWpStateChange.start)) {
      latestChange = latestWpStateChange;
    }
    setLastStateChange(latestChange);
  }, [
    store.workplaceStore.selectedWorkplace,
    store.operationStateLogStore.logs.length,
    store.workplaceStateLogStore.logs.length,
  ]);

  const openModal = (startTransitionId, operationId) => {
    setCurrentStartTransitionId(startTransitionId);
    setCurrentOperationId(operationId);
    setModalVisible(true);
  };

  const closeModal = () => {
    setCurrentStartTransitionId(undefined);
    setCurrentOperationId(undefined);
    setModalVisible(false);
  };

  const afterExecuteTransition = () => {
    setSelectedOperation(undefined);
    setTransitionConfirmVisible(false);
    store.relevantMessagesStore.loadRelevantMessages();
  };

  const handleHasPreviousOperation = (previousOperation, validStartTransition) => {
    const previousOperationIsStarted = Boolean(previousOperation?.actualStart);

    if (!previousOperationIsStarted) {
      setTransitionConfirmVisible(true);
    } else {
      openModal(validStartTransition.id, selectedOperation.id);
    }
  };

  const handleHasNoPreviousOperation = async (validStartTransition) => {
    const hasParentOrder = Boolean(selectedOperation.order?.parentOrderId);

    if (hasParentOrder) {
      const parentOperations = await store.operationStore.loadAllWithDependencies({
        params: {orderId: selectedOperation.order.parentOrderId},
      });

      const sortedParentOperations = sortBy(parentOperations, ['no'], ['asc']);

      if (sortedParentOperations.length) {
        handleHasPreviousOperation(sortedParentOperations[sortedParentOperations.length - 1], validStartTransition);
      } else {
        openModal(validStartTransition.id, selectedOperation.id);
      }
    } else {
      openModal(validStartTransition.id, selectedOperation.id);
    }
  };

  const validateTransition = (validStartTransition) => {
    const sortedOperationsOfSelectedOrder = sortBy(
      store.operationStore.operations.filter((o) => o.orderId === selectedOperation.orderId),
      ['no'],
      ['asc']
    );

    const workplace = store.workplaceStore.getById(selectedOperation.workplaceId);
    const parent = workplace.hierarchyId ? store.hierarchyStore.getById(workplace.hierarchyId).parent : undefined;

    let posOfSelectedOperation = sortedOperationsOfSelectedOrder.findIndex((o) => o.id === selectedOperation.id);

    if (parent) {
      const parentWorkplace = store.workplaceStore.getByHierarchyId(parent.id);
      if (parentWorkplace?.isVirtualRoot) {
        posOfSelectedOperation = sortedOperationsOfSelectedOrder.findIndex((o) => o.workplaceId === parentWorkplace.id);
      } else if (parentWorkplace) {
        posOfSelectedOperation = undefined;
      }
    }

    const posOfPreviousOperation = posOfSelectedOperation ? (posOfSelectedOperation - 1) : -1;
    const hasPreviousOperation = posOfPreviousOperation >= 0;

    if (hasPreviousOperation) {
      handleHasPreviousOperation(sortedOperationsOfSelectedOrder[posOfPreviousOperation], validStartTransition);
    } else {
      handleHasNoPreviousOperation(validStartTransition);
    }
  };

  const canStartTransition = () => {
    let canStart = policy.canExecute();
    if (canStart && !startAnyPolicy.canExecute()) {
      // Paused operations can always be restarted
      if (selectedOperation.displayStatus === Operation.displayStates.paused) {
        canStart = true;
      } else {
        // filter by type and date (only use operations that are displayed).
        const listStart = moment().subtract(settings.loadDaysPast, 'days').startOf('day');
        const listEnd = moment().add(settings.loadDaysFuture, 'days').startOf('day');
        const plannedOperations = store.operationStore.aggregatedOperations
          .filter((op) => op.isPlanned
            && listStart.isBefore(op.plannedEnd) && listEnd.isAfter(op.plannedStart));
        if (plannedOperations.length > 0) {
          if (plannedOperations[0].children?.length > 0) {
            canStart = plannedOperations[0].children[0].id === selectedOperation.id;
          } else {
            canStart = plannedOperations[0].id === selectedOperation.id;
          }
        } else {
          canStart = false;
        }
      }
    }
    return canStart;
  };

  const confirmForceStart = (validStartTransition) => {
    setForcedStart(true);
    setTransitionConfirmVisible(false);
    openModal(validStartTransition.id, selectedOperation.id);
  };

  const handleTransitionApiError = () => {
    const fromDate = moment().subtract(settings.loadDaysPast, 'days').startOf('day');
    const toDate = moment().add(settings.loadDaysFuture, 'days').startOf('day');
    store.operationStore.loadPlannedOperationsByWorkplace(
      store.workplaceStore.selectedWorkplace.id,
      {
        params: {
          fromDate: fromDate.toISOString(),
          toDate: toDate.toISOString(),
        },
      }
    );
  };

  return (
    <Modal
      width="90%"
      title={(
        <>
          <FontAwesomeIcon icon={faBagsShopping}/>
          {' '}
          {selectedOperation ? `${t('order.model.one')} ${selectedOperation.order?.no}` : t('order.model.other')}
        </>
      )}
      footer={false}
      onCancel={() => {
        setIsReordering(false);
        onCancel();
        setShowOperationDetails(false);
      }}
      {...props}
    >
      {showOperationDetails && selectedOperation
        ? <OrderDetails operations={[selectedOperation]} widgetConfig={widgetConfig}/>
        : (
          <OrderTable
            workplaceId={workplaceId}
            identifier={identifier}
            settings={settings}
            columnsPlanned={columnsPlanned}
            columnsFinished={columnsFinished}
            isModalTable
            useSelectedOperation={[selectedOperation, setSelectedOperation]}
            useIsReordering={[isReordering, setIsReordering]}
            setShowOperationDetails={setShowOperationDetails}
          />
        )}
      {
        !isReordering && !showOperationDetails && selectedOperation
        && Boolean(store.validStartTransitionsStore.transitions.length)
        && (
          <Row justify="end" style={{marginTop: '1em'}}>
            {
              store.validStartTransitionsStore.transitions.map((validStartTransition) => (
                <Col key={validStartTransition.id}>
                  <Popconfirm
                    title={t('orderWidget.transitionConfirm')}
                    visible={transitionConfirmVisible}
                    onConfirm={() => confirmForceStart(validStartTransition)}
                    okButtonProps={{loading: store.transitionStore.isLoading(TransitionStoreActions.performTransition)}}
                    onCancel={() => setTransitionConfirmVisible(false)}
                  >
                    <Button
                      type="primary"
                      onClick={() => validateTransition(validStartTransition)}
                      disabled={!canStartTransition()}
                    >
                      {validStartTransition.name}
                    </Button>
                  </Popconfirm>
                </Col>
              ))
            }
          </Row>
        )
      }
      {modalVisible && (
        <StateTransitionWidgetDetailModal
          fullscreen={store.clientStore.isMobile}
          title={t('stateTransitionWidget.modal.title')}
          onCancel={closeModal}
          onApiError={handleTransitionApiError}
          transitionId={currentStartTransitionId}
          afterExecuteTransition={afterExecuteTransition}
          afterSuccessfulTransition={onCancel}
          startingOperationId={currentOperationId}
          isForceStarted={forcedStart}
          lastStateChange={lastStateChange}
        />
      )}
    </Modal>
  );
};

export default observer(OrderWidgetModal);
